import { type ReactNode, useEffect, useState } from "react";

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Finest unit the countdown resolves to. Drives both the tick rate and
 * the output format:
 *
 *   - "minutes" (default): ticks every 60s, formats `Xd Yh Zm`
 *     (respecting `truncate` + the `<1m` floor). The historical
 *     `Data/Countdown` behavior.
 *   - "seconds": ticks every 1s, formats the live clock `Xd HH:MM:SS`
 *     (the former standalone `formatCountdown` output).
 *   - "hours" / "days": coarser truncations of the minutes format —
 *     ticks every 60s, drops the finer units.
 */
export type CountdownPrecision = "days" | "hours" | "minutes" | "seconds";

const TICK_FOR_PRECISION: Record<CountdownPrecision, number> = {
    days: MINUTE_MS,
    hours: MINUTE_MS,
    minutes: MINUTE_MS,
    seconds: SECOND_MS
};

export interface CountdownOptions {
    /**
     * Finest unit shown. Defaults to "minutes" (the historical
     * `Data/Countdown` behavior). Also selects the tick rate and the
     * output format — see `CountdownPrecision`.
     */
    precision?: CountdownPrecision;
    /**
     * Override the tick interval derived from `precision`. Rarely
     * needed — `precision` already picks a sensible rate (60s for
     * days/hours/minutes, 1s for seconds).
     */
    tickMs?: number;
    /**
     * "two-unit" (default): pick the two largest non-zero units, so
     * `12d 5h 30m` prints as `12d 5h` and `5h 30m 0s` as `5h 30m`.
     * Two-unit reads cleaner on a card surface than three.
     * "all": print every non-zero d/h/m unit, no truncation.
     *
     * Only applies to the "days"/"hours"/"minutes" formats — the
     * "seconds" precision always renders the full `Xd HH:MM:SS` clock.
     */
    truncate?: "two-unit" | "all";
}

const pad = (n: number): string => String(n).padStart(2, "0");

/**
 * Formats the remaining milliseconds into the string for the given
 * precision. Returns `null` only when the caller has already decided
 * there is nothing to show; the `<1m` floor is handled inline so a
 * still-positive sub-minute countdown never collapses to null.
 */
function formatRemaining(
    ms: number,
    precision: CountdownPrecision,
    truncate: "two-unit" | "all"
): string {
    if (precision === "seconds") {
        // Live clock: `Xd HH:MM:SS` with a leading `Xd ` only once a
        // full day remains. Matches the former `formatCountdown` util.
        const total = Math.floor(ms / SECOND_MS);
        const d = Math.floor(total / 86400);
        const h = Math.floor((total % 86400) / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        const clock = `${pad(h)}:${pad(m)}:${pad(s)}`;
        return d > 0 ? `${d}d ${clock}` : clock;
    }

    const days = Math.floor(ms / DAY_MS);
    const hours = Math.floor((ms % DAY_MS) / HOUR_MS);
    const minutes = Math.floor((ms % HOUR_MS) / MINUTE_MS);

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    // For coarser precisions, drop the finer units entirely.
    if (precision !== "days" && hours > 0) parts.push(`${hours}h`);
    if (precision !== "days" && precision !== "hours" && minutes > 0) {
        parts.push(`${minutes}m`);
    }

    if (parts.length === 0) {
        // Less than one unit of the requested precision remaining —
        // explicit floor so we don't collapse to null with a still-
        // positive countdown.
        if (precision === "days") return "<1d";
        if (precision === "hours") return "<1h";
        return "<1m";
    }

    return truncate === "two-unit"
        ? parts.slice(0, 2).join(" ")
        : parts.join(" ");
}

/**
 * Returns the formatted countdown string from now to `target`, or
 * `null` once the target has passed (or when target is null/undefined).
 * Re-renders on a self-managed tick interval — drop into any component
 * without wiring a parent ticker.
 *
 * The single countdown mechanism: `precision` tunes how fine the
 * readout is (days / hours / minutes / seconds), which also drives the
 * tick rate and the output format. Defaults to minute precision.
 *
 * Pure value: caller decides how to render it. Pair with the `Countdown`
 * component below for the common "render the string in a wrapper" case,
 * or use this hook directly when more control is needed.
 */
export function useCountdown(
    target: string | Date | null | undefined,
    options: CountdownOptions = {}
): string | null {
    const { precision = "minutes", truncate = "two-unit" } = options;
    const tickMs = options.tickMs ?? TICK_FOR_PRECISION[precision];

    const targetMs =
        target === null || target === undefined
            ? null
            : target instanceof Date
              ? target.getTime()
              : new Date(target).getTime();

    const [, setTick] = useState(0);
    useEffect(() => {
        if (targetMs === null) return;
        const id = window.setInterval(() => {
            setTick((t) => t + 1);
        }, tickMs);
        return (): void => window.clearInterval(id);
    }, [targetMs, tickMs]);

    if (targetMs === null) return null;
    const ms = targetMs - Date.now();
    if (ms <= 0) return null;

    return formatRemaining(ms, precision, truncate);
}

export interface CountdownProps extends CountdownOptions {
    target: string | Date | null | undefined;
    /**
     * Rendered when target is null/undefined or has passed. Defaults
     * to `null` (renders nothing).
     */
    placeholder?: ReactNode;
}

/**
 * Generic countdown text — renders a text node only, no element
 * wrapper, no opinion on typography or color. Wrap with whatever
 * styled element fits the surface:
 *
 * ```tsx
 * <Text variant="small" className="text-foreground-base tabular-nums">
 *   <Countdown target={iso} placeholder="—" />
 * </Text>
 * ```
 *
 * Self-tickers via `useCountdown` (default 1-minute interval; pass
 * `precision="seconds"` for a live 1s clock).
 */
export function Countdown({
    target,
    placeholder = null,
    ...options
}: CountdownProps): ReactNode {
    const formatted = useCountdown(target, options);
    return formatted ?? placeholder;
}
